/** FAQ accordion. FAQ_ITEMS is exported for the FAQPage JSON-LD. */
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';

export const FAQ_ITEMS = [
  {
    q: 'What is RotaHQ?',
    a: 'RotaHQ is restaurant workforce-management software. It brings staff rotas, employee scheduling, clock-in/out, attendance, worked hours, payroll preparation and staff resources together in one simple platform.',
  },
  {
    q: 'Is RotaHQ designed for restaurants?',
    a: 'Yes. RotaHQ is built specifically for restaurants, cafés and hospitality teams — shift-based rotas, a clock-in kiosk for the floor, and attendance that maps to service hours.',
  },
  {
    q: 'Is there a free trial?',
    a: 'Yes — every account starts with a 14-day free trial with no setup fee, so you can build a rota and try the kiosk with your own team before choosing a plan.',
  },
  {
    q: 'Is pricing per employee?',
    a: 'No. RotaHQ has flat monthly pricing per plan. Starter (£19/month) includes up to 10 employees, Pro (£49/month) up to 50, and Business (£149/month) has no employee limit.',
  },
  {
    q: 'Can employees clock in and out?',
    a: 'Yes. Employees clock in and out on the RotaHQ kiosk — any tablet or phone in the restaurant — using their personal PIN. Managers can enable location controls where appropriate.',
  },
  {
    q: 'Can I manage multiple locations?',
    a: 'Each location runs its own RotaHQ workspace with its own rota, kiosk and team. If you operate more than one site, set up a workspace per location — book a demo and we’ll help you get started.',
  },
  {
    q: 'Do I need technical knowledge?',
    a: 'No. RotaHQ runs in the browser with nothing to install. Setup takes minutes, and you can import your whole staff roster from a CSV or Excel file.',
  },
  {
    q: 'How much does restaurant rota software cost?',
    a: 'RotaHQ starts at \u00a319 per month (VAT included) for the Starter plan. Pro is \u00a349/month and Business is \u00a3149/month \u2014 all flat prices with no per-employee fees, and yearly billing gives you 2 months free.',
  },
  {
    q: 'Can staff see their rota on their phone?',
    a: 'Yes. Every employee gets access to the RotaHQ employee portal, which works in any phone browser. They can see their published shifts, worked hours, leave requests and payslips.',
  },
  {
    q: 'Does RotaHQ calculate payroll hours?',
    a: 'Yes. Worked hours flow straight from attendance into payroll drafts, so you review real clocked hours instead of retyping them. Pro and Business plans include payroll runs with overtime and UK tax and National Insurance estimates \u2014 you always review and finalise before paying.',
  },
  {
    q: 'Can I import my existing staff list?',
    a: 'Yes. Upload a CSV or Excel file with names, roles and hourly rates, preview every row before anything is saved, and choose how to handle any duplicates.',
  },
  {
    q: 'What happens after the free trial ends?',
    a: 'Your 14-day trial needs no card, so nothing is ever charged automatically. When the trial ends you simply pick a plan to continue \u2014 your rotas, staff and attendance data are kept safe in the meantime.',
  },
  {
    q: 'How do staff and managers get notified?',
    a: 'RotaHQ has a built-in notification bell plus optional browser push notifications on any device. Staff are alerted when rotas are published or shifts change, and get a reminder about an hour before their shift. Managers get alerts for missed clock-outs, possible no-shows and a daily attendance summary \u2014 no phone numbers or extra apps needed.',
  },
  {
    q: 'Is RotaHQ GDPR compliant?',
    a: 'RotaHQ is built for UK GDPR: a Data Processing Agreement is published on the site, passwords are stored hashed, and built-in tools let you export or delete personal data on request. See our Privacy Policy for full details.',
  },
];

export function FaqSection() {
  return (
    <section id="faq" className="scroll-mt-24 [content-visibility:auto]">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-14 sm:py-20">
        <h2 className="text-3xl sm:text-4xl font-display font-semibold tracking-tight text-center" data-testid="marketing-faq-heading">
          Frequently asked questions
        </h2>
        <Accordion type="single" collapsible className="mt-8" data-testid="marketing-faq">
          {FAQ_ITEMS.map((item, i) => (
            <AccordionItem key={item.q} value={`faq-${i}`} className="border-[color:var(--brand-border)]">
              <AccordionTrigger className="text-left font-semibold text-sm sm:text-base py-4 hover:no-underline hover:text-[color:var(--brand-primary)]" data-testid={`marketing-faq-item-${i}-trigger`}>
                {item.q}
              </AccordionTrigger>
              <AccordionContent className="text-sm sm:text-base text-[color:var(--brand-text-muted)] leading-relaxed" data-testid={`marketing-faq-item-${i}-content`}>
                {item.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </section>
  );
}
